import { useEffect, useState, useCallback } from "react";
import { BarChart3, Users, Search, Sparkles, CreditCard, Eye, MessageSquare, Loader2, Trash2, Wrench, Mail, Globe } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import PricingSettingsPanel from "@/components/PricingSettingsPanel";
import api from "@/lib/api";

const RANGES = [7, 30, 90];

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-[#121212] border border-white/10 rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wide">
      <Icon size={14} className="text-[#4da3ff]" /> {label}
    </div>
    <div className="font-heading text-2xl font-bold text-white mt-2">{value ?? "—"}</div>
  </div>
);

const List = ({ title, items }) => (
  <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
    <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-3">{title}</h3>
    {items?.length ? (
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-zinc-300 font-mono truncate">{it.label}</span>
            <span className="text-[#4da3ff] font-mono shrink-0">{it.count}</span>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-zinc-600 text-sm">No data yet.</p>
    )}
  </div>
);

export default function Admin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [fixOrders, setFixOrders] = useState([]);
  const [customers, setCustomers] = useState(null);
  const [freeScans, setFreeScans] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, f, u, fs] = await Promise.all([
        api.get(`/admin/analytics?days=${days}`),
        api.get("/admin/suggestions"),
        api.get("/admin/fix-orders"),
        api.get("/admin/users"),
        api.get(`/admin/free-scans?days=${days}`),
      ]);
      setData(a.data);
      setSuggestions(s.data.suggestions || []);
      setFixOrders(f.data.orders || []);
      setCustomers(u.data);
      setFreeScans(fs.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const setFixStatus = async (order, status) => {
    try {
      await api.post("/admin/fix-orders/status", { user_id: order.user_id, domain: order.domain, status });
      setFixOrders((prev) => prev.map((o) => (o.user_id === order.user_id && o.domain === order.domain ? { ...o, fix_status: status } : o)));
      toast.success(`${order.domain} → ${status.replace("_", " ")}`);
    } catch { toast.error("Update failed"); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/suggestions/${id}?status=${status}`);
      setSuggestions((prev) => prev.map((x) => (x.suggestion_id === id ? { ...x, status } : x)));
    } catch { toast.error("Update failed"); }
  };

  const resetAnalytics = async () => {
    if (!window.confirm("Reset all traffic analytics? This permanently clears pageviews, sessions and event history and cannot be undone.")) return;
    try {
      const res = await api.post("/admin/analytics/reset");
      toast.success(`Analytics reset — cleared ${res.data.deleted} events.`);
      load();
    } catch { toast.error("Reset failed"); }
  };

  const t = data?.totals || {};

  return (
    <Layout>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-2">
          <BarChart3 size={22} className="text-[#4da3ff]" />
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-white">Traffic & Analytics</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reset-analytics-btn"
            onClick={resetAnalytics}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#FF453A]/40 text-[#FF6961] hover:bg-[#FF453A]/10 px-3 py-1.5 text-sm transition-colors"
          >
            <Trash2 size={14} /> Reset
          </button>
          <div className="inline-flex items-center gap-1 bg-[#121212] border border-white/10 rounded-full p-1" data-testid="range-toggle">
            {RANGES.map((r) => (
              <button
                key={r}
                data-testid={`range-${r}`}
                onClick={() => setDays(r)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${days === r ? "bg-[#007AFF] text-white" : "text-zinc-400"}`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#007AFF]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <Stat icon={Eye} label="Pageviews" value={t.pageviews} />
            <Stat icon={Users} label="Sessions" value={t.sessions} />
            <Stat icon={Sparkles} label="Signups" value={t.signups} />
            <Stat icon={Search} label="Scans" value={t.scans} />
            <Stat icon={Sparkles} label="AI Briefs" value={t.aiBriefs} />
            <Stat icon={CreditCard} label="Checkouts" value={t.checkouts} />
          </div>

          {/* Customers — who signed up + what they did + how to reach them */}
          <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="customers-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-[#4da3ff]" />
                <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Customers</h3>
              </div>
              <div className="flex items-center gap-4 text-xs text-zinc-400">
                <span><b className="text-white">{customers?.totals?.users ?? 0}</b> total</span>
                <span><b className="text-[#32D74B]">{customers?.totals?.paying ?? 0}</b> paying</span>
                <span><b className="text-white">${customers?.totals?.revenue ?? 0}</b> revenue</span>
                <a href={`${api.defaults.baseURL}/admin/users`} target="_blank" rel="noreferrer" className="text-[#4da3ff] hover:underline hidden">raw</a>
              </div>
            </div>
            {customers?.users?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                      <th className="py-2 pr-3">Customer</th>
                      <th className="py-2 px-3">Plan</th>
                      <th className="py-2 px-3">Scans</th>
                      <th className="py-2 px-3">Websites</th>
                      <th className="py-2 px-3">Spent</th>
                      <th className="py-2 px-3">Joined</th>
                      <th className="py-2 pl-3 text-right">Reach out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.users.map((c) => (
                      <tr key={c.user_id} className="border-b border-white/5" data-testid={`customer-row-${c.email}`}>
                        <td className="py-2.5 pr-3">
                          <div className="text-white font-medium">{c.name || "—"}</div>
                          <div className="text-zinc-500 text-xs font-mono">{c.email}</div>
                          {c.domains?.length ? <div className="text-zinc-600 text-[11px] mt-0.5 truncate max-w-[220px]">{c.domains.join(", ")}</div> : null}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase ${c.plan === "free" ? "bg-white/10 text-zinc-400" : "bg-[#32D74B]/15 text-[#32D74B]"}`}>{c.plan}</span>
                        </td>
                        <td className="py-2.5 px-3 text-zinc-300 font-mono">{c.scansCount}</td>
                        <td className="py-2.5 px-3 text-zinc-300 font-mono">{c.domainsCount}</td>
                        <td className="py-2.5 px-3 font-mono" style={{ color: c.revenue > 0 ? "#32D74B" : "#71717a" }}>${c.revenue}</td>
                        <td className="py-2.5 px-3 text-zinc-500 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                        <td className="py-2.5 pl-3 text-right">
                          <a href={`mailto:${c.email}?subject=${encodeURIComponent("Link Website Pro")}`}
                            data-testid={`email-customer-${c.email}`}
                            className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-lg px-2.5 py-1 text-xs transition-colors">
                            <Mail size={12} /> Email
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-zinc-600 text-sm">No customers yet.</p>
            )}
          </div>

          {/* Free Scans — the domains people scanned (your warmest leads) */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#121212] border border-white/10 rounded-xl p-5 md:col-span-2" data-testid="free-scans-panel">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Search size={16} className="text-[#4da3ff]" />
                  <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Free scans ({days}d)</h3>
                </div>
                <span className="text-xs text-zinc-400"><b className="text-white">{freeScans?.total ?? 0}</b> scans · <b className="text-white">{freeScans?.unique ?? 0}</b> unique sites</span>
              </div>
              {freeScans?.recent?.length ? (
                <div className="max-h-72 overflow-y-auto space-y-1.5">
                  {freeScans.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5" data-testid={`free-scan-${i}`}>
                      <span className="text-zinc-200 font-mono truncate flex items-center gap-1.5"><Globe size={12} className="text-zinc-600 shrink-0" />{r.domain}</span>
                      <span className="text-zinc-600 text-xs shrink-0">{r.ip} · {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-zinc-600 text-sm">No free scans in this range.</p>}
            </div>
            <List title="Most-scanned (hot leads)" items={(freeScans?.topDomains || []).map((d) => ({ label: d.domain, count: d.count }))} />
          </div>


          <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="traffic-chart">
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-4">Pageviews & Sessions</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data?.series || []} margin={{ left: -20, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9b5cff" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#9b5cff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff" }} />
                <Area type="monotone" dataKey="views" name="Pageviews" stroke="#007AFF" fill="url(#gv)" strokeWidth={2} />
                <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#9b5cff" fill="url(#gs)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <List title="Top Pages" items={data?.topPages} />
            <List title="Top Referrers" items={data?.topReferrers} />
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="suggestions-panel">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={16} className="text-[#4da3ff]" />
              <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Suggestion Box ({suggestions.length})</h3>
            </div>
            {suggestions.length ? (
              <div className="space-y-3">
                {suggestions.map((s) => (
                  <div key={s.suggestion_id} className="border border-white/10 rounded-lg p-3.5" data-testid={`suggestion-${s.suggestion_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#4da3ff] bg-[#007AFF]/10 border border-[#007AFF]/30 rounded px-2 py-0.5">{s.category}</span>
                      <span className="text-zinc-400 text-xs">{s.email}</span>
                      <span className="text-zinc-600 text-xs">· {new Date(s.created_at).toLocaleDateString()}</span>
                      <select
                        data-testid={`suggestion-status-${s.suggestion_id}`}
                        value={s.status}
                        onChange={(e) => setStatus(s.suggestion_id, e.target.value)}
                        className="ml-auto bg-[#0A0A0A] border border-white/15 rounded text-xs text-zinc-300 px-2 py-1"
                      >
                        {["new", "reviewed", "done", "archived"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <p className="text-zinc-200 text-sm mt-2 leading-relaxed">{s.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-600 text-sm">No suggestions yet.</p>
            )}
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="fix-orders-panel">
            <div className="flex items-center gap-2 mb-4">
              <Wrench size={16} className="text-[#32D74B]" />
              <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Guided Fix Orders ({fixOrders.length})</h3>
            </div>
            {fixOrders.length ? (
              <div className="space-y-2.5">
                {fixOrders.map((o) => (
                  <div key={`${o.user_id}-${o.domain}`} className="flex items-center justify-between gap-3 border border-white/10 rounded-lg p-3.5 flex-wrap" data-testid={`fix-order-${o.domain}`}>
                    <div className="min-w-0">
                      <div className="text-white text-sm font-mono truncate">{o.domain}</div>
                      <div className="text-zinc-500 text-xs truncate">{o.email || o.user_id}{o.fix_purchased_at ? ` · ${new Date(o.fix_purchased_at).toLocaleDateString()}` : ""}</div>
                    </div>
                    <select
                      data-testid={`fix-order-status-${o.domain}`}
                      value={o.fix_status || "requested"}
                      onChange={(e) => setFixStatus(o, e.target.value)}
                      className="bg-[#0A0A0A] border border-white/15 rounded text-xs text-zinc-200 px-2 py-1.5"
                    >
                      <option value="requested">Requested</option>
                      <option value="in_progress">In progress</option>
                      <option value="done">Done</option>
                    </select>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-600 text-sm">No Guided Fix orders yet.</p>
            )}
          </div>

          <PricingSettingsPanel />
        </>
      )}
    </Layout>
  );
}
